"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WindowBase = void 0;
const event_emitter_1 = require("../lib/event_emitter");
const edge_1 = require("./enums/edge");
const window_type_1 = require("./enums/window_type");
const window_state_controller_1 = require("./internal/window_state_controller");
const window_positioning_1 = require("./positioning/window_positioning");
const default_options_1 = require("./utils/default_options");
const monitor_state_controller_1 = require("./internal/monitor_state_controller");
const monitor_helper_1 = require("./utils/monitor_helper");
const logger_service_1 = require("../common/logging/logger_service");
class WindowBase extends event_emitter_1.EventEmitter {
    /**
     * WindowBase C'tor
     * @param options new window options (null when open existing window)
     * @param id when open existing window (for internal use)
     */
    constructor(options, id) {
        super();
        this.logger = logger_service_1.LoggerService.getLogger().getChildCategory('WindowBase');
        // ---------------------------------------------------------------------------
        this.onWindowResized = (window) => {
            if (window.id !== this.id) {
                return;
            }
            this.owWindowInfo = window;
            if (!window.logicalBounds) {
                return;
            }
            this.fire('resized', {
                width: window.logicalBounds.width,
                height: window.logicalBounds.height,
            });
        };
        // ---------------------------------------------------------------------------
        this.onWindowMoved = (window) => {
            if (window.id !== this.id) {
                return;
            }
            this.owWindowInfo = window;
            this.isDragging = false;
            this.performAnchoring();
            this.fire('moved', {
                x: window.logicalBounds.left,
                y: window.logicalBounds.top,
            });
        };
        // ---------------------------------------------------------------------------
        this.onWindowDPIChanged = (args) => {
            var _a;
            if (((_a = args === null || args === void 0 ? void 0 : args.window) === null || _a === void 0 ? void 0 : _a.id) !== this.id) {
                return;
            }
            const { window, prevDPI, newDPI } = args;
            this.owWindowInfo = window;
            this.performAutoZoom(prevDPI, newDPI);
        };
        // ---------------------------------------------------------------------------
        this.onWindowDragStarted = (window) => {
            if (window.id !== this.id) {
                return;
            }
            this.owWindowInfo = window;
            this.isDragging = true;
            this.logger.debug(`start dragging ${this.id}`);
        };
        // ---------------------------------------------------------------------------
        this.onWindowReadyToShow = (window) => {
            if (window.id !== this.id) {
                return;
            }
            this.fire('ready-to-show');
        };
        // ---------------------------------------------------------------------------
        this.onWindowLoadError = (window) => {
            if (window.id !== this.id) {
                return;
            }
            this.fire('load-error');
        };
        // open existing
        if (id != null) {
            this.id = id;
            return;
        }
        this.setDefaultOption(options);
        // when options is null, create for current
        if (options == null) {
            this.creationPromise = this.openInternal();
            return;
        }
        // if (!options?.url) {
        //   this.options = options;
        //   return;
        // }
        this.creationPromise = this.createInternal(options);
    }
    // ---------------------------------------------------------------------------
    setDefaultOption(options) {
        (0, default_options_1.applyDefaultOptions)(options, this.type());
    }
    // ---------------------------------------------------------------------------
    saveWindowOptions(options, id) {
        this.backgroundWindow =
            this.backgroundWindow || overwolf.windows.getMainWindow();
        // saving odk window using background window (cross window)
        if (this.backgroundWindow.___odkinternal___ === undefined) {
            this.backgroundWindow.___odkinternal___ = {}; // any
        }
        if (!this.backgroundWindow.___odkinternal___[id]) {
            this.backgroundWindow.___odkinternal___[id] = options;
            this.options = this.backgroundWindow.___odkinternal___[id];
        }
    }
    // ---------------------------------------------------------------------------
    // read existing open window options from background window
    readWindowOptions(id) {
        // make sure background sync window is assigned
        this.backgroundWindow =
            this.backgroundWindow || overwolf.windows.getMainWindow();
        this.options = this.backgroundWindow.___odkinternal___[id];
        this.logger.debug(`read window ${id} options: ${JSON.stringify(this.options)}`);
    }
    // ---------------------------------------------------------------------------
    async awaitableTimeout(callback, ms) {
        return new Promise((resolve, reject) => {
            setTimeout(async () => {
                resolve(await callback());
            }, ms);
        });
    }
    // ---------------------------------------------------------------------------
    Id() {
        return this.id;
    }
    // ---------------------------------------------------------------------------
    loadUrl(url) {
        return new Promise(async (resolve) => {
            if (!url) {
                resolve(false);
            }
            this.options.url = url;
            if (this.id) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                overwolf.windows2.loadUrl(this.id, url, result => {
                    resolve(result.success);
                });
                return;
            }
            await this.createInternal(this.options);
            resolve(!!this.id);
        });
    }
    // ---------------------------------------------------------------------------
    async setTopmost(isTopmost) {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.setTopmost(this.id, isTopmost, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve();
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async center() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            const monitor = await monitor_helper_1.MonitorHelper.getWindowMonitor(this);
            return window_positioning_1.WindowPositioning.centerWindow(this, monitor);
        });
    }
    // ---------------------------------------------------------------------------
    async zoom(factor) {
        await this.assureCreated();
        return new Promise(async (resolve) => {
            overwolf.windows.setZoom(factor, this.id);
            resolve();
        });
    }
    // ---------------------------------------------------------------------------
    async mute(mute, all) {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            if (all) {
                overwolf.windows.muteAll(result => {
                    if (!result.success) {
                        reject();
                    }
                    else {
                        resolve();
                    }
                });
            }
            else {
                overwolf.windows.setMute(mute, result => {
                    if (!result.success) {
                        reject(result.error);
                    }
                    else {
                        resolve();
                    }
                });
            }
        });
    }
    // ---------------------------------------------------------------------------
    async close() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.close(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve();
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async show() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.restore(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve(true);
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async hide() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.hide(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve();
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async minimize() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.minimize(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve();
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async maximize() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.maximize(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve();
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async restore() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.restore(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve();
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async setPosition(point) {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.changePosition(this.id, point.x, point.y, result => {
                resolve(result.success);
            });
        });
    }
    // ---------------------------------------------------------------------------
    async setSize(size) {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.changeSize(this.id, size.width, size.height, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    resolve(true);
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    async setBounds(rect) {
        await this.assureCreated();
        // const bounds = this.getBounds();
        return new Promise(async (resolve, reject) => {
            var _a;
            overwolf.windows.setBounds({
                window_id: this.id,
                bounds: {
                    width: !isNaN(rect.width) ? rect.width : null,
                    height: !isNaN(rect.height) ? rect.height : null,
                    x: !isNaN(rect.y) ? rect.x : null,
                    y: !isNaN(rect.y) ? rect.y : null,
                },
                auto_dpi_resize: (_a = this.options) === null || _a === void 0 ? void 0 : _a.autoDpi,
            }, result => {
                resolve(result.success);
            });
        });
    }
    // ---------------------------------------------------------------------------
    async dock(dock, margin, monitor) {
        await this.assureCreated();
        if (!dock) {
            return;
        }
        if (!monitor) {
            monitor = await monitor_helper_1.MonitorHelper.getWindowMonitor(this);
        }
        await window_positioning_1.WindowPositioning.setDockPosition({
            window: this,
            edge: dock,
            marginOptions: margin,
            monitor: monitor,
        });
    }
    // ---------------------------------------------------------------------------
    async anchor(dock, marginOptions) {
        var _a;
        await this.assureCreated();
        if (!dock) {
            return;
        }
        if (!dock || dock === edge_1.Edge.None) {
            this.options.anchoring = null;
            (_a = this.monitorStateController) === null || _a === void 0 ? void 0 : _a.dispose();
            this.monitorStateController = null;
            return;
        }
        if (!this.monitorStateController) {
            this.monitorStateController = new monitor_state_controller_1.MonitorStateController(this);
        }
        this.options.anchoring = {
            window: this,
            edge: dock,
            marginOptions: marginOptions,
        };
        this.performAnchoring();
    }
    // ---------------------------------------------------------------------------
    getBounds() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        this.assureCreated();
        const width = (_c = (_b = (_a = this.owWindowInfo) === null || _a === void 0 ? void 0 : _a.logicalBounds) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : NaN;
        const height = (_f = (_e = (_d = this.owWindowInfo) === null || _d === void 0 ? void 0 : _d.logicalBounds) === null || _e === void 0 ? void 0 : _e.height) !== null && _f !== void 0 ? _f : NaN;
        return {
            x: (_j = (_h = (_g = this.owWindowInfo) === null || _g === void 0 ? void 0 : _g.logicalBounds) === null || _h === void 0 ? void 0 : _h.left) !== null && _j !== void 0 ? _j : NaN,
            y: (_m = (_l = (_k = this.owWindowInfo) === null || _k === void 0 ? void 0 : _k.logicalBounds) === null || _l === void 0 ? void 0 : _l.top) !== null && _m !== void 0 ? _m : NaN,
            width: width,
            height: height,
            dpiAwareWidth: !isNaN(width) ? width * this.owWindowInfo.dpiScale : NaN,
            dpiAwareHeight: !isNaN(height)
                ? height * this.owWindowInfo.dpiScale
                : NaN,
        };
    }
    // ---------------------------------------------------------------------------
    // start dragging
    async move() {
        await this.assureCreated();
        return new Promise(async (resolve, reject) => {
            overwolf.windows.dragMove(this.id, result => {
                if (!result.success) {
                    reject(result.error);
                }
                else {
                    this.isDragging = false;
                    resolve(true);
                }
            });
        });
    }
    // ---------------------------------------------------------------------------
    // create for existing window
    get desktopOnly() {
        return this.type() === window_type_1.WindowType.Desktop;
    }
    // ---------------------------------------------------------------------------
    // create for existing window
    openInternal() {
        this.logger.debug('open existing window');
        if (this.id) {
            throw new Error('Window already created');
        }
        this.backgroundWindow = overwolf.windows.getMainWindow();
        this.creationPromise = new Promise((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            overwolf.windows2.create(null, null, result => {
                this.logger.debug(`open existing window result: ${JSON.stringify(result)}`);
                if (!result.success) {
                    this.clearWindowOptions();
                    this.id = null;
                    reject(result.error);
                }
                else {
                    this.readWindowOptions(result.window.id);
                    this.onWindowCreated(result.window);
                    resolve();
                }
            });
        });
        return this.creationPromise;
    }
    // ---------------------------------------------------------------------------
    createInternal(options) {
        if (this.id) {
            throw new Error('Window already created');
        }
        if (!(options === null || options === void 0 ? void 0 : options.id)) {
            throw new Error('ID must be provided');
        }
        const { id } = options;
        // creating new window, save setting so
        // Windows.Self() || Windows.FromId(...) will be up to date
        this.saveWindowOptions(options, id);
        this.creationPromise = new Promise((resolve, reject) => {
            overwolf.windows2.create(id, options, result => {
                // console.log('overwolf.windows2:', result);
                if (!result.success) {
                    this.clearWindowOptions();
                    this.id = null;
                    reject(result.error);
                }
                else {
                    this.onWindowCreated(result.window);
                    resolve();
                }
            });
        });
        return this.creationPromise;
    }
    // ---------------------------------------------------------------------------
    async assureCreated() {
        if (this.creationPromise) {
            await this.creationPromise;
        }
        if (closed) {
            throw new Error('Window closed');
        }
        if (!this.id) {
            throw new Error('Window not created');
        }
    }
    // ---------------------------------------------------------------------------
    onWindowCreated(owWindow) {
        this.logger.info(`window created: ${owWindow.id}`);
        this.id = owWindow.id;
        this.owWindowInfo = owWindow;
        this.windowStateController = new window_state_controller_1.WindowStateController(this);
        overwolf.windows2.resized.addListener(this.onWindowResized);
        overwolf.windows2.moved.addListener(this.onWindowMoved);
        overwolf.windows2.dragStarted.addListener(this.onWindowDragStarted);
        overwolf.windows2.dpiChanged.addListener(this.onWindowDPIChanged);
        overwolf.windows2.readyToShow.addListener(this.onWindowReadyToShow);
        overwolf.windows2.loadError.addListener(this.onWindowLoadError);
        this.positionNewWindow();
    }
    // ---------------------------------------------------------------------------
    async performAnchoring(monitor) {
        var _a;
        if (this.options == null || ((_a = this.options) === null || _a === void 0 ? void 0 : _a.anchoring) == null) {
            return;
        }
        if (this.isDragging) {
            this.logger.info('not performing anchoring while dragging');
            return;
        }
        if (!monitor) {
            monitor = await monitor_helper_1.MonitorHelper.getWindowMonitor(this);
        }
        const { edge: dock, marginOptions: margin } = this.options.anchoring;
        this.dock(dock, margin, monitor);
    }
    // ---------------------------------------------------------------------------
    performAutoZoom(prevDPI, newDPI) {
        var _a;
        if (!((_a = this.options) === null || _a === void 0 ? void 0 : _a.autoZoom)) {
            return;
        }
        if (newDPI === 1) {
            this.zoom(0);
        }
        else {
            const factor = newDPI > prevDPI ? -1 : 1;
            this.zoom((factor * newDPI) / prevDPI);
        }
    }
    // ---------------------------------------------------------------------------
    async positionNewWindow() {
        var _a, _b, _c, _d, _e;
        if (((_a = this.options) === null || _a === void 0 ? void 0 : _a.x) !== undefined && ((_b = this.options) === null || _b === void 0 ? void 0 : _b.x) !== undefined) {
            return;
        }
        if (((_c = this.options) === null || _c === void 0 ? void 0 : _c.anchoring) !== undefined) {
            await this.anchor(this.options.anchoring.edge, this.options.anchoring.marginOptions);
            return;
        }
        if (((_d = this.options) === null || _d === void 0 ? void 0 : _d.dockPosition) !== undefined) {
            await this.dock((_e = this.options) === null || _e === void 0 ? void 0 : _e.dockPosition);
            return;
        }
        await this.center();
    }
    // ---------------------------------------------------------------------------
    clearWindowOptions() {
        try {
            if (this.backgroundWindow && this.id) {
                this.backgroundWindow.___odkinternal___[this.id] = null;
            }
        }
        catch (err) {
            this.logger.warn(`error resetting window ${this.id} options: ${err}`);
        }
        this.backgroundWindow = null;
        this.options = null;
    }
    // ---------------------------------------------------------------------------
    // @internal
    get windowInfo() {
        return this.owWindowInfo;
    }
    // ---------------------------------------------------------------------------
    // @internal
    get windowOptions() {
        return this.options;
    }
    // ---------------------------------------------------------------------------
    // @internal
    onWindowClosed() {
        var _a, _b;
        this.logger.info(`window ${this.id} closed`);
        this.closed = true;
        (_a = this.windowStateController) === null || _a === void 0 ? void 0 : _a.dispose();
        this.windowStateController = null;
        (_b = this.monitorStateController) === null || _b === void 0 ? void 0 : _b.dispose();
        this.monitorStateController = null;
        overwolf.windows2.resized.removeListener(this.onWindowResized);
        overwolf.windows2.moved.removeListener(this.onWindowMoved);
        overwolf.windows2.dpiChanged.removeListener(this.onWindowDPIChanged);
        overwolf.windows2.readyToShow.removeListener(this.onWindowReadyToShow);
        overwolf.windows2.loadError.removeListener(this.onWindowLoadError);
        overwolf.windows2.dragStarted.removeListener(this.onWindowDragStarted);
        this.clearWindowOptions();
        this.fire('closed', this);
        this.id = null;
    }
    // ---------------------------------------------------------------------------
    // @internal
    onWindowMonitorPropertyChanged(monitor, window) {
        this.owWindowInfo = window;
        this.performAnchoring(monitor);
    }
    on(event, listener) {
        super.on(event, listener);
    }
    once(event, listener) {
        super.once(event, listener);
    }
}
exports.WindowBase = WindowBase;
//# sourceMappingURL=window_base.js.map